import { useEffect, useState } from "react";
import { Share, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const platform = () => {
  const ua = window.navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

const STEPS = {
  ios: ["Open this page in Safari", "Tap the Share button", "Choose “Add to Home Screen”"],
  android: ["Open the browser menu (⋮)", "Tap “Install app” or “Add to Home screen”", "Confirm"],
  desktop: ["Click the install icon in the address bar (a screen with an arrow)",
            "Or open the browser menu and choose “Install…”",
            "The app opens in its own window and pins to your dock or taskbar"],
};

export const InstallPrompt = ({ companyName, blurb, testId = "install-prompt" }) => {
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const kind = platform();
  // Inside a store build this is already an installed app.
  const native = Boolean(process.env.REACT_APP_NATIVE_HOME);

  useEffect(() => {
    const capture = (e) => { e.preventDefault(); setEvent(e); };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  if (native || dismissed || isStandalone()) return null;

  return (
    <div data-testid={testId} className="relative rounded-xl border border-gold/30 bg-gold/5 p-5 text-left">
      <button data-testid="install-dismiss" onClick={() => setDismissed(true)}
        className="absolute right-3 top-3 text-slate-500 hover:text-slate-300">
        <X className="h-4 w-4" />
      </button>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <Smartphone className="h-3.5 w-3.5" /> Install {companyName}
      </p>
      <p className="mt-2 pr-6 text-sm text-slate-300">
        {blurb || `Add ${companyName} to your home screen so it opens like a real app — full screen, one tap, no browser bar.`}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {event && (
          <Button data-testid="install-app-btn"
            onClick={async () => { await event.prompt(); setDismissed(true); }}
            className="bg-gold text-ink-900 hover:bg-gold-light">
            Install now
          </Button>
        )}
        <button data-testid="install-how" onClick={() => setShowSteps(!showSteps)}
          className="font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
          {showSteps ? "Hide the steps" : "How do I install it?"}
        </button>
      </div>

      {showSteps && (
        <ol data-testid="install-steps" className="mt-4 space-y-2 border-t border-gold/20 pt-4">
          {STEPS[kind].map((step, i) => (
            <li key={step} className="flex gap-3 text-xs text-slate-400">
              <span className="font-mono text-gold">{i + 1}</span>
              <span className="flex items-center gap-1.5">
                {kind === "ios" && i === 1 && <Share className="h-3.5 w-3.5 text-gold" />}
                {step}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
